import React from "react";
import { PayrollBreakdownStatus, RepetitionType, SummaryRow } from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import PayrollBreakdownSummary from "../../screens/client/PayrollBreakdownSummary";

const ROWS_PRESETS: SummaryRow[] = [
    {
        category: "bonus",
        description: "Gros bonus",
        detailedDescription: null,
        productKey: "bonus",
        quantity: 1,
        unitPrice: 77700,
        repetition: null,
    },
    {
        productKey: "fee_other",
        category: "dynamic_fee",
        description: "Chaussettes",
        detailedDescription: null,
        repetition: RepetitionType.Count,
        quantity: 1,
        unitPrice: 6000,
    },
    {
        category: "dynamic_fee",
        description: "Trajet à Martigny",
        detailedDescription: null,
        productKey: "fee_other",
        repetition: RepetitionType.Count,
        quantity: 3,
        unitPrice: 2500,
    },
    {
        category: "dynamic_fee",
        description: "Trajet professionnel",
        detailedDescription: null,
        productKey: "fee_other",
        repetition: RepetitionType.WorkedDay,
        quantity: 2,
        unitPrice: 4000,
    },
    {
        category: "dynamic_fee",
        description: "Abonnement téléphone",
        detailedDescription: null,
        productKey: "fee_other",
        repetition: RepetitionType.WorkedMonth,
        quantity: 2,
        unitPrice: 5000,
    },
    {
        category: "effective_fee",
        description: "Disque dur SSD",
        detailedDescription: null,
        productKey: "fee_other",
        quantity: 1,
        unitPrice: 13000,
        repetition: null,
    },
    {
        category: "effective_fee",
        description: "Chaussures de ski",
        detailedDescription: null,
        productKey: "fee_other",
        quantity: 1,
        unitPrice: 12000,
        repetition: null,
    },
    {
        category: "deduction",
        description: "Repas de midi",
        detailedDescription: null,
        productKey: "fee_other",
        quantity: 21,
        unitPrice: -1000,
        repetition: null,
    },
    {
        category: "variable_fee",
        description: "km voiture privé",
        // Detailed description as the backend sends it: its amount is formatted with a
        // French locale, and has to be displayed the Swiss way like the rest of the summary.
        detailedDescription: "km voiture privé [200.0 × 0,18 CHF]",
        productKey: "fee_other",
        quantity: 200,
        unitPrice: 18,
        repetition: RepetitionType.Count,
    },
];

const StoryComponent = ({ salaryRows = [] }: { salaryRows: SummaryRow[] }) => {
    return (
        <PayrollBreakdownSummary
            payrollBreakdownStatus={PayrollBreakdownStatus.Open}
            rows={[...salaryRows, ...ROWS_PRESETS]}
            loading={false}
        />
    );
};

const meta = {
    title: "Screens/PayrollBreakdownSummary",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const SalaryMonthlyFull: Story = {
    args: {
        salaryRows: [
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_monthly_full",
                quantity: 1,
                unitPrice: 654300,
                repetition: null,
            },
        ],
    },
};

export const SalaryMonthlyPartial: Story = {
    args: {
        salaryRows: [
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_monthly_partial",
                quantity: 1,
                unitPrice: 432100,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_0",
                quantity: 30,
                unitPrice: 2000,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_25",
                quantity: 20,
                unitPrice: 500,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_50",
                quantity: 10,
                unitPrice: 1000,
                repetition: null,
            },
        ],
    },
};

export const SalaryDaily: Story = {
    args: {
        salaryRows: [
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_daily",
                quantity: 1,
                unitPrice: 50000,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_25",
                quantity: 20,
                unitPrice: 500,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_50",
                quantity: 10,
                unitPrice: 1000,
                repetition: null,
            },
        ],
    },
};

export const SalaryHourly: Story = {
    args: {
        salaryRows: [
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_hourly",
                quantity: 1,
                unitPrice: 6000,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_25",
                quantity: 20,
                unitPrice: 500,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_50",
                quantity: 10,
                unitPrice: 1000,
                repetition: null,
            },
        ],
    },
};

export const SalaryPackage: Story = {
    args: {
        salaryRows: [
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_package",
                quantity: 1,
                unitPrice: 123400,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_25",
                quantity: 20,
                unitPrice: 500,
                repetition: null,
            },
            {
                category: "salary",
                description: null,
                detailedDescription: null,
                productKey: "salary_overtime_50",
                quantity: 10,
                unitPrice: 1000,
                repetition: null,
            },
        ],
    },
};
